import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { productsAPI, customersAPI, salesAPI, saleMediaAPI } from '../services/api';
import SalePhotoUploader from '../components/branch/SalePhotoUploader';
import { getBranchProductDisplayPrices, isProductSellable } from '../utils/branchProductPrice';

const PAYMENT_MODES = ['Cash', 'EMI'];
const EMI_MONTHS = [1, 2, 3, 4, 5, 6];

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function NewSale() {
  const navigate = useNavigate();
  const [qs] = useSearchParams();
  const printRef = useRef(null);

  const [step, setStep] = useState('form'); // form | preview | done
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [savedSale, setSavedSale] = useState(null);

  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showProductList, setShowProductList] = useState(false);

  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '' });
  const [addingNewCustomer, setAddingNewCustomer] = useState(false);

  const [salePrice, setSalePrice] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [downPayment, setDownPayment] = useState('');
  const [downPaymentTouched, setDownPaymentTouched] = useState(false);
  const [emiMonths, setEmiMonths] = useState(3);
  const [emiStartDate, setEmiStartDate] = useState(new Date().toISOString().split('T')[0]);

  // Witness fields for EMI
  const [witnessName,  setWitnessName]  = useState('');
  const [witnessPhone, setWitnessPhone] = useState('');
  const [witnessSearch, setWitnessSearch] = useState('');
  const [witnessResults, setWitnessResults] = useState([]);
  const [showWitnessList, setShowWitnessList] = useState(false);
  const [witnessOnlyFilter, setWitnessOnlyFilter] = useState(false);
  const [selectedWitnessCustomer, setSelectedWitnessCustomer] = useState(null);
  const [salePhoto, setSalePhoto] = useState(null); // optional sale-moment photo

  const suggestedDownPayment = useMemo(() => {
    const sp = parseFloat(salePrice) || 0;
    if (sp <= 0) return 0;
    return Math.round(sp * 0.5);
  }, [salePrice]);

  const emiDownAmount = useMemo(() => {
    const dp = parseFloat(downPayment);
    if (paymentMode !== 'EMI' || !Number.isFinite(dp) || dp < 0) return 0;
    return Math.round(dp);
  }, [downPayment, paymentMode]);

  const monthlyAmount = useMemo(() => {
    const sp = parseFloat(salePrice) || 0;
    if (paymentMode !== 'EMI' || sp <= 0 || emiMonths <= 0) return 0;
    const principal = Math.max(0, sp - emiDownAmount);
    if (principal <= 0) return 0;
    return Math.ceil(principal / emiMonths);
  }, [salePrice, emiDownAmount, emiMonths, paymentMode]);

  // Sale price change → suggest 50% down (until user edits manually)
  useEffect(() => {
    if (paymentMode !== 'EMI') return;
    const sp = parseFloat(salePrice) || 0;
    if (sp <= 0) {
      if (!downPaymentTouched) setDownPayment('');
      return;
    }
    if (!downPaymentTouched) {
      setDownPayment(String(Math.round(sp * 0.5)));
    }
  }, [salePrice, paymentMode, downPaymentTouched]);

  const applySuggestedDownPayment = () => {
    const sp = parseFloat(salePrice) || 0;
    if (sp > 0) {
      setDownPayment(String(Math.round(sp * 0.5)));
      setDownPaymentTouched(false);
    }
  };

  /** Pehli EMI: calendar date ke agle mahine wahi din (backend jaisa) */
  const firstEmiDueLabel = useMemo(() => {
    if (paymentMode !== 'EMI' || !emiStartDate) return '';
    const a = new Date(emiStartDate + 'T12:00:00');
    if (Number.isNaN(a.getTime())) return '';
    const y = a.getFullYear();
    const m = a.getMonth();
    const day = a.getDate();
    const targetM = m + 1;
    const lastDay = new Date(y, targetM + 1, 0).getDate();
    const d = Math.min(day, lastDay);
    const due = new Date(y, targetM, d);
    return due.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  }, [paymentMode, emiStartDate]);

  // Preselect product via ?productId=
  useEffect(() => {
    const pid = qs.get('productId');
    if (pid) {
      productsAPI.getById(pid).then(res => {
        const p = res.data.product;
        if (p) {
          if (!isProductSellable(p)) {
            setError('Ye product abhi sale ke liye ready nahi hai — pehle Products se sale price set karein.');
            return;
          }
          selectProduct(p);
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  // Product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch.length >= 1) loadProducts(productSearch);
    }, 260);
    return () => clearTimeout(timer);
  }, [productSearch]);

  const loadProducts = async (search) => {
    setLoadingProducts(true);
    try {
      const res = await productsAPI.list({ search });
      const all = res.data.products || [];
      setProducts(all.filter(isProductSellable));
      setShowProductList(true);
    } catch {
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const selectProduct = (p) => {
    setSelectedProduct(p);
    setProductSearch(p.name);
    setShowProductList(false);
    const { salePrice: defaultSale } = getBranchProductDisplayPrices(p);
    if (!salePrice && defaultSale > 0) setSalePrice(String(defaultSale));
  };

  // Customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch.length >= 3) searchCustomers(customerSearch);
      else setCustomerResults([]);
    }, 350);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const searchCustomers = async (q) => {
    try {
      const res = await customersAPI.list({ search: q, limit: 8 });
      setCustomerResults(res.data.customers || []);
      setShowCustomerList(true);
    } catch {
      setCustomerResults([]);
    }
  };

  // Witness search (same customers; optional “witness contacts only” filter)
  useEffect(() => {
    if (paymentMode !== 'EMI') return;
    const timer = setTimeout(() => {
      if (witnessSearch.length >= 2) searchWitnesses(witnessSearch);
      else { setWitnessResults([]); setShowWitnessList(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [witnessSearch, witnessOnlyFilter, paymentMode]);

  const searchWitnesses = async (q) => {
    try {
      const res = await customersAPI.list({
        search: q,
        limit: 12,
        ...(witnessOnlyFilter ? { witnessOnly: '1' } : {})
      });
      let rows = res.data.customers || [];
      const buyerId = selectedCustomer?._id;
      if (buyerId) rows = rows.filter(r => String(r._id) !== String(buyerId));
      setWitnessResults(rows);
      setShowWitnessList(true);
    } catch {
      setWitnessResults([]);
    }
  };

  const selectWitnessFromSearch = (w) => {
    if (selectedCustomer && String(selectedCustomer._id) === String(w._id)) {
      setError('Buyer and witness must be different people.');
      return;
    }
    setSelectedWitnessCustomer(w);
    setWitnessSearch(`${w.name} — ${w.phone}`);
    setWitnessName(w.name || '');
    setWitnessPhone(w.phone || '');
    setShowWitnessList(false);
    setError('');
  };

  const clearWitnessPick = () => {
    setSelectedWitnessCustomer(null);
    setWitnessSearch('');
    setWitnessName('');
    setWitnessPhone('');
    setWitnessResults([]);
    setShowWitnessList(false);
  };

  const selectCustomer = async (c) => {
    setSelectedCustomer(c);
    setCustomerSearch(c.name + ' — ' + c.phone);
    setShowCustomerList(false);
    setAddingNewCustomer(false);
    // Clear previous witness pick so stale data doesn't carry over
    clearWitnessPick();

    // Pull full profile: if a stored witness phone exists, try to find that customer
    try {
      const res = await customersAPI.get(c._id);
      const cust = res.data.customer;
      const w0 = cust?.branchKyc?.witnesses && cust.branchKyc.witnesses[0];
      if (w0?.name && w0?.phone) {
        setWitnessName(w0.name);
        setWitnessPhone(w0.phone);
        setWitnessSearch(`${w0.name} — ${w0.phone}`);
        // Try to find matching saved customer so witnessCustomerId is sent → reward points work
        try {
          const wRes = await customersAPI.list({ search: w0.phone, limit: 5 });
          const match = (wRes.data.customers || []).find(
            cu => String(cu.phone).trim() === String(w0.phone).trim() && String(cu._id) !== String(c._id)
          );
          if (match) setSelectedWitnessCustomer(match);
        } catch { /* non-blocking */ }
      }
    } catch {
      setWitnessName('');
      setWitnessPhone('');
    }
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    clearWitnessPick();
  };

  const validateForm = () => {
    if (!selectedProduct) return 'Select a product first.';
    const sp = parseFloat(salePrice);
    if (!sp || sp <= 0) return 'Enter a valid sale price.';
    if (selectedProduct.minSellingPrice && sp < selectedProduct.minSellingPrice) {
      return `Minimum selling price is ${fmt(selectedProduct.minSellingPrice)}.`;
    }
    if (paymentMode === 'EMI') {
      if (!emiMonths || emiMonths < 1 || emiMonths > 6) return 'EMI term must be between 1 and 6 months.';
      if (sp < 2) return 'Sale price is too low for EMI.';
      const dp = parseFloat(downPayment);
      if (!Number.isFinite(dp) || dp <= 0) return 'Down payment amount enter karein.';
      if (dp >= sp) return 'Down payment sale price se kam honi chahiye.';

      if (!selectedCustomer && !addingNewCustomer) return 'EMI sale ke liye customer select karein ya naya add karein.';

      if (addingNewCustomer) {
        if (!newCustomer.name.trim() || !newCustomer.phone.trim()) return 'Customer name and phone are required for EMI.';
      }

      if (!witnessName.trim() || !witnessPhone.trim()) return 'Witness name + phone EMI ke liye zaroori hai.';
    } else {
      if (!selectedCustomer && !addingNewCustomer) {
        return 'Customer name and phone required for audit trail.';
      }
      if (addingNewCustomer && (!newCustomer.name.trim() || !newCustomer.phone.trim())) {
        return 'Customer name and phone required.';
      }
    }
    return null;
  };

  const handlePreview = () => {
    const err = validateForm();
    if (err) { setError(err); return; }
    setError('');
    setStep('preview');
  };

  const resolveOrCreateCustomer = async () => {
    if (selectedCustomer) return selectedCustomer;
    if (!addingNewCustomer) return null;
    const res = await customersAPI.create({
      name: newCustomer.name.trim(),
      phone: newCustomer.phone.trim()
    }).catch(e => {
      if (e.response?.data?.customer) return { data: { customer: e.response.data.customer } };
      throw e;
    });
    return res.data.customer;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const cust = await resolveOrCreateCustomer();

      if (paymentMode === 'EMI' && !cust?._id) {
        throw new Error('Could not create or locate the customer.');
      }

      const payload = {
        productId: selectedProduct._id,
        customerName: cust?.name || newCustomer.name.trim(),
        customerPhone: cust?.phone || newCustomer.phone.trim(),
        ...(cust?._id ? { customerId: cust._id } : {}),
        salePrice: parseFloat(salePrice),
        paymentMode,
        ...(paymentMode === 'EMI' ? {
          emiMonths: Number(emiMonths),
          downPayment: emiDownAmount,
          monthlyAmount,
          emiStartDate,
          witnessName: witnessName.trim(),
          witnessPhone: witnessPhone.trim(),
          ...(selectedWitnessCustomer?._id ? { witnessCustomerId: selectedWitnessCustomer._id } : {})
        } : {})
      };

      const res = await salesAPI.create(payload);
      const saleResult = res.data.sale || {};

      // Upload optional sale-moment photo (fire-and-forget — don't block success)
      if (salePhoto) {
        try {
          const fd = new FormData();
          fd.append('salePhoto', salePhoto);
          if (cust?._id) fd.append('customerId', cust._id);
          fd.append('saleType', paymentMode === 'EMI' ? 'emi' : 'cash');
          fd.append('saleRef', saleResult.emiId || '');
          await saleMediaAPI.upload(fd);
        } catch (_) { /* non-blocking */ }
      }

      setSavedSale({
        ...payload,
        downPayment: paymentMode === 'EMI' ? emiDownAmount : undefined,
        productName: selectedProduct.name,
        category: selectedProduct.category,
        ...saleResult,
        saleDate: new Date().toLocaleString('en-IN')
      });
      setStep('done');
    } catch (err) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.message || err.message || 'Could not save the sale. Please try again.';
      const detail = err.response?.data?.error;
      setError(code ? `[${code}] ${msg}` : (detail ? `${msg} (${detail})` : msg));
      setStep('form');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep('form'); setSelectedProduct(null); setProductSearch('');
    clearCustomer(); setNewCustomer({ name: '', phone: '' }); setAddingNewCustomer(false);
    setWitnessOnlyFilter(false);
    setSalePhoto(null);
    setSalePrice(''); setPaymentMode('Cash'); setDownPayment(''); setDownPaymentTouched(false);
    setEmiMonths(3);
    setSavedSale(null); setError('');
  };

  // ────────── DONE / INVOICE ──────────
  if (step === 'done' && savedSale) {
    return (
      <div>
        <div className="form-card mb-3" ref={printRef}>
          <div className="text-center mb-3">
            <div style={{ fontSize: 40 }}>✓</div>
            <h4 className="fw-700" style={{ color: '#198754' }}>Sale recorded successfully</h4>
            <p className="text-muted small mb-0">Commission ledger auto-updated. WhatsApp receipts queued.</p>
          </div>
          <div style={{ border: '2px solid #0d6efd', borderRadius: 12, padding: 20 }}>
            <div className="text-center mb-2">
              <h5 className="fw-700 text-brand">A2zTak · Etawah Branch</h5>
              <small className="text-muted">Sale receipt — {savedSale.saleDate}</small>
            </div>
            <hr />
            <div className="row g-2" style={{ fontSize: 14 }}>
              <div className="col-6"><strong>Product:</strong></div>
              <div className="col-6 text-end">{savedSale.productName}</div>
              <div className="col-6"><strong>Customer:</strong></div>
              <div className="col-6 text-end">{savedSale.customerName || '—'}</div>
              <div className="col-6"><strong>Phone:</strong></div>
              <div className="col-6 text-end">{savedSale.customerPhone || '—'}</div>
              <div className="col-6"><strong>Payment:</strong></div>
              <div className="col-6 text-end">
                <span className={`chip ${savedSale.paymentMode === 'EMI' ? 'badge-emi' : 'badge-cash'}`}>{savedSale.paymentMode}</span>
              </div>
              <div className="col-6"><strong>Sale price:</strong></div>
              <div className="col-6 text-end fw-700 text-brand" style={{ fontSize: 16 }}>{fmt(savedSale.salePrice)}</div>
              {savedSale.paymentMode === 'EMI' && (
                <>
                  <div className="col-6"><strong>Down payment:</strong></div>
                  <div className="col-6 text-end">{fmt(savedSale.downPayment)}</div>
                  <div className="col-6"><strong>EMI:</strong></div>
                  <div className="col-6 text-end">{fmt(savedSale.monthlyAmount)} × {savedSale.emiMonths} months</div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-primary" onClick={() => window.print()}>Print receipt</button>
          <button className="btn btn-outline-primary" onClick={resetForm}>+ Another sale</button>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/my-sales')}>View sales</button>
          {savedSale.emiId && (
            <button className="btn btn-outline-success" onClick={() => navigate('/emi')}>Go to EMI collections</button>
          )}
        </div>
      </div>
    );
  }

  // ────────── PREVIEW ──────────
  if (step === 'preview') {
    const sp = parseFloat(salePrice) || 0;
    const dp = emiDownAmount;
    return (
      <div>
        <div className="form-card mb-3">
          <h5 className="fw-700 mb-3">Confirm sale</h5>
          <div className="row g-2" style={{ fontSize: 15 }}>
            <div className="col-5 text-muted">Product</div>
            <div className="col-7 fw-700">{selectedProduct?.name}</div>
            <div className="col-5 text-muted">Category</div>
            <div className="col-7">{selectedProduct?.category}</div>
            <div className="col-5 text-muted">Customer</div>
            <div className="col-7 fw-700">{selectedCustomer?.name || newCustomer.name} — {selectedCustomer?.phone || newCustomer.phone}</div>
            <div className="col-5 text-muted">Sale price</div>
            <div className="col-7 fw-700 text-brand" style={{ fontSize: 17 }}>{fmt(sp)}</div>
            <div className="col-5 text-muted">Payment</div>
            <div className="col-7"><span className={`chip ${paymentMode === 'EMI' ? 'badge-emi' : 'badge-cash'}`}>{paymentMode}</span></div>
            {paymentMode === 'EMI' && (
              <>
                <div className="col-5 text-muted">Down payment</div>
                <div className="col-7">{fmt(dp)}</div>
                <div className="col-5 text-muted">EMI</div>
                <div className="col-7">{fmt(monthlyAmount)} × {emiMonths} months</div>
                {firstEmiDueLabel && (
                  <>
                    <div className="col-5 text-muted">1st EMI due</div>
                    <div className="col-7 fw-600" style={{ color: '#2e7d32' }}>{firstEmiDueLabel}</div>
                  </>
                )}
                <div className="col-5 text-muted">Witness</div>
                <div className="col-7">{witnessName} · {witnessPhone}</div>
              </>
            )}
          </div>
          {error && <div className="alert-soft danger mt-3">{error}</div>}
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary flex-fill py-2" onClick={() => setStep('form')}>Back</button>
          <button className="btn btn-success flex-fill py-2" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Confirm & Save'}
          </button>
        </div>
      </div>
    );
  }

  // ────────── FORM ──────────
  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0 fw-700">New Sale</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Branch portal · EMI down payment (50% suggested) · 1–6 month EMI
          </div>
        </div>
      </div>

      {error && <div className="alert-soft danger mb-3">{error}</div>}

      {/* 1. Product */}
      <div className="form-card mb-3">
        <div className="step-head"><span className="step-n">1</span> Product</div>
        <div className="mb-2 position-relative">
          <input
            type="text"
            className="form-control"
            placeholder="Search by product, brand, category…"
            value={productSearch}
            onChange={e => { setProductSearch(e.target.value); setSelectedProduct(null); }}
            onFocus={() => productSearch.length >= 1 && setShowProductList(true)}
          />
          {loadingProducts && (
            <div style={{ position: 'absolute', right: 12, top: 10 }}>
              <div className="spinner-border spinner-border-sm text-secondary" />
            </div>
          )}
          {showProductList && products.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
              boxShadow: '0 10px 28px rgba(16,24,40,0.12)', maxHeight: 280, overflowY: 'auto', marginTop: 4
            }}>
              {products.map(p => (
                <div key={p._id} onClick={() => selectProduct(p)}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {p.brand} · {p.category} · Stock {p.stock}
                    {p.minSellingPrice ? ` · Min ${fmt(p.minSellingPrice)}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {selectedProduct && (
          <div className="alert-soft info">
            Selected: <strong>{selectedProduct.name}</strong> · {selectedProduct.brand} · Stock {selectedProduct.stock}
            {selectedProduct.minSellingPrice ? ` · Min price ${fmt(selectedProduct.minSellingPrice)}` : ''}
          </div>
        )}
      </div>

      {/* 2. Customer */}
      <div className="form-card mb-3">
        <div className="step-head"><span className="step-n">2</span> Customer</div>
        <div className="mb-2 position-relative">
          <input
            type="text"
            className="form-control"
            placeholder="Search name or phone (3+ chars)…"
            value={customerSearch}
            onChange={e => { setCustomerSearch(e.target.value); setSelectedCustomer(null); setAddingNewCustomer(false); }}
          />
          {showCustomerList && customerResults.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
              boxShadow: '0 10px 28px rgba(16,24,40,0.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4
            }}>
              {customerResults.map(c => (
                <div key={c._id} onClick={() => selectCustomer(c)}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {c.phone} {c.city ? `· ${c.city}` : ''}
                    {c.kycSummary?.kycComplete ? ' · KYC complete' : ' · KYC pending'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedCustomer ? (
          <div className="alert-soft info">
            <strong>{selectedCustomer.name}</strong> · {selectedCustomer.phone}
            <button className="btn btn-sm btn-link p-0 ms-2" onClick={clearCustomer}>Change</button>
          </div>
        ) : (
          <div>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => { setAddingNewCustomer(!addingNewCustomer); setCustomerSearch(''); }}
            >
              {addingNewCustomer ? 'Cancel' : '+ Add new customer'}
            </button>
            {addingNewCustomer && (
              <div className="row g-2 mt-2">
                <div className="col-md-6">
                  <input className="form-control" placeholder="Customer name *"
                    value={newCustomer.name}
                    onChange={e => setNewCustomer(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <input className="form-control" placeholder="Phone *" type="tel"
                    value={newCustomer.phone}
                    onChange={e => setNewCustomer(p => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3. Sale + Payment */}
      <div className="form-card mb-3">
        <div className="step-head"><span className="step-n">3</span> Sale details</div>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">
              Sale price (₹) *
              {selectedProduct?.minSellingPrice && (
                <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                  Min {fmt(selectedProduct.minSellingPrice)}
                </span>
              )}
            </label>
            <input
              type="number"
              className="form-control"
              placeholder="e.g. 15000"
              value={salePrice}
              onChange={e => setSalePrice(e.target.value)}
              min={selectedProduct?.minSellingPrice || 0}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Payment mode *</label>
            <div className="d-flex gap-2 mt-1">
              {PAYMENT_MODES.map(m => (
                <button
                  key={m}
                  type="button"
                  className="btn flex-fill py-2"
                  style={{
                    borderRadius: 8, fontWeight: 600,
                    border: paymentMode === m ? '2px solid var(--brand-primary)' : '2px solid #e5e7eb',
                    background: paymentMode === m ? 'var(--brand-primary)' : '#fff',
                    color: paymentMode === m ? '#fff' : '#374151'
                  }}
                  onClick={() => {
                    setPaymentMode(m);
                    if (m === 'EMI') {
                      setDownPaymentTouched(false);
                      const sp = parseFloat(salePrice) || 0;
                      if (sp > 0) setDownPayment(String(Math.round(sp * 0.5)));
                    } else {
                      setDownPayment('');
                      setDownPaymentTouched(false);
                    }
                  }}
                >
                  {m === 'Cash' ? 'Cash (UPI included)' : 'EMI'}
                </button>
              ))}
            </div>
            <div className="text-muted" style={{ fontSize: 11, marginTop: 6 }}>
              UPI is treated as Cash for reporting.
            </div>
          </div>
        </div>

        {paymentMode === 'EMI' && (
          <div className="mt-4 p-3" style={{ background: '#fff8e1', borderRadius: 12, border: '1.5px solid #ffe082' }}>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div className="fw-700" style={{ color: '#b45309' }}>EMI details</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Down payment default <strong>50%</strong> — manually change kar sakte ho. EMI tenure 1–6 months.
                </div>
              </div>
              <span className="chip badge-emi">Mandatory KYC</span>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label d-flex justify-content-between align-items-center">
                  <span>Down payment (₹) *</span>
                  {suggestedDownPayment > 0 && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      style={{ fontSize: 11 }}
                      onClick={applySuggestedDownPayment}
                    >
                      Use 50% ({fmt(suggestedDownPayment)})
                    </button>
                  )}
                </label>
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  max={Math.max(0, (parseFloat(salePrice) || 0) - 1)}
                  value={downPayment}
                  onChange={(e) => {
                    setDownPaymentTouched(true);
                    setDownPayment(e.target.value);
                  }}
                  placeholder={suggestedDownPayment > 0 ? String(suggestedDownPayment) : 'e.g. 6000'}
                />
                {suggestedDownPayment > 0 && (
                  <div className="form-text">Suggested: {fmt(suggestedDownPayment)} (50% of sale price)</div>
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label">EMI months</label>
                <select className="form-select" value={emiMonths} onChange={e => setEmiMonths(e.target.value)}>
                  {EMI_MONTHS.map(m => <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">EMI calendar date (reference)</label>
                <input type="date" className="form-control" value={emiStartDate} onChange={e => setEmiStartDate(e.target.value)} />
                {firstEmiDueLabel && (
                  <div className="mt-2 p-2 rounded" style={{ background: '#e8f5e9', fontSize: 12, border: '1px solid #a5d6a7' }}>
                    <strong>Pehli EMI due:</strong> {firstEmiDueLabel}
                    <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                      Chahe aaj ki date select karo — pehli kist <em>agle mahine</em> se shuru (yehi din, month end par adjust).
                      Baaki <strong>{emiMonths}</strong> kistayn isi pattern se.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {salePrice && (
              <div className="mt-3 p-2" style={{ background: '#fff', borderRadius: 8 }}>
                <div className="row g-1 text-center">
                  <div className="col-4">
                    <div className="text-muted" style={{ fontSize: 12 }}>Sale price</div>
                    <div className="fw-700 text-brand">{fmt(salePrice)}</div>
                  </div>
                  <div className="col-4">
                    <div className="text-muted" style={{ fontSize: 12 }}>Down payment</div>
                    <div className="fw-700" style={{ color: '#198754' }}>{fmt(emiDownAmount)}</div>
                  </div>
                  <div className="col-4">
                    <div className="text-muted" style={{ fontSize: 12 }}>Monthly × {emiMonths}</div>
                    <div className="fw-700" style={{ color: '#b45309' }}>{fmt(monthlyAmount)}</div>
                  </div>
                </div>
              </div>
            )}

            <hr className="my-3" />

            <div className="fw-700 mb-2">Witness (required for EMI)</div>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Witness — search saved customer (name / phone)</label>
                <div className="d-flex flex-wrap gap-2 mb-1">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="witnessOnly"
                      checked={witnessOnlyFilter}
                      onChange={e => { setWitnessOnlyFilter(e.target.checked); setWitnessSearch(''); setWitnessResults([]); }}
                    />
                    <label className="form-check-label" htmlFor="witnessOnly">Witness contacts only</label>
                  </div>
                  {selectedWitnessCustomer && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearWitnessPick}>Clear witness</button>
                  )}
                </div>
                <div className="position-relative">
                  <input
                    className="form-control"
                    placeholder="Type 2+ characters to search…"
                    value={witnessSearch}
                    onChange={e => {
                      setWitnessSearch(e.target.value);
                      setSelectedWitnessCustomer(null);
                    }}
                    onFocus={() => witnessSearch.length >= 2 && witnessResults.length > 0 && setShowWitnessList(true)}
                  />
                  {showWitnessList && witnessResults.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                      boxShadow: '0 10px 28px rgba(16,24,40,0.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4
                    }}>
                      {witnessResults.map(w => (
                        <div
                          key={w._id}
                          onClick={() => selectWitnessFromSearch(w)}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                        >
                          <div style={{ fontWeight: 600 }}>{w.name}</div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {w.phone}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <small className="text-muted">Witness bhi normal customer hi hota hai. Profile me checkbox on karke quick lookup ke liye mark kar sakte hain.</small>
              </div>
              <div className="col-md-6">
                <label className="form-label">Witness name (or edit after search)</label>
                <input className="form-control" value={witnessName} onChange={e => { setWitnessName(e.target.value); setSelectedWitnessCustomer(null); }} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Witness phone</label>
                <input className="form-control" value={witnessPhone} onChange={e => { setWitnessPhone(e.target.value); setSelectedWitnessCustomer(null); }} />
              </div>
            </div>

            <div className="text-muted mt-2" style={{ fontSize: 11, lineHeight: 1.4 }}>
              EMI requires: buyer on file and witness name + phone (from search or typed).
            </div>
          </div>
        )}
      </div>

      {/* Sale-moment photo — optional, stored per sale in customer profile */}
      <div className="form-card mb-3">
        <div className="step-head">
          <span className="step-n">📷</span> Sale Photo
          <span className="text-muted fw-400 ms-2" style={{ fontSize: 12 }}>(optional)</span>
        </div>
        <div className="row g-2 align-items-center">
          <div className="col-md-6">
            <label className="form-label">Customer holding the product</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/*"
              className="form-control"
              onChange={e => setSalePhoto(e.target.files[0] || null)}
            />
            <small className="text-muted">
              Photo is stored per sale — visible in the customer's profile gallery. Useful for future reference.
            </small>
          </div>
          {salePhoto && (
            <div className="col-md-4">
              <img
                src={URL.createObjectURL(salePhoto)}
                alt="preview"
                style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 10, border: '2px solid #e5e7eb' }}
              />
              <button type="button" className="btn btn-sm btn-outline-secondary mt-1 w-100"
                onClick={() => setSalePhoto(null)}>Remove</button>
            </div>
          )}
        </div>
      </div>

      <div className="d-flex gap-2">
        <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Cancel</button>
        <button className="btn btn-primary flex-fill" onClick={handlePreview}>Preview sale →</button>
      </div>
    </div>
  );
}
